/**
 * @file useSectionFormValue.ts
 * @description Значение формы секции для полей редактора (phase19.16, SB120
 * этапы 1 и 3а): замечания проверки и предупреждения о текстах, подсветка,
 * живой предпросмотр, переход к полю, предупреждения оформления
 * (`useSectionLookWarnings`) и элементов (`useSectionElementWarnings` —
 * «больше 3 шрифтов» с учётом своих шрифтов остальных блоков страницы).
 * Вынесено из `SectionBlockEditor`, чтобы компонент оставался ≤ 200 строк.
 */

import type { SectionPayload, SiteTheme } from "@chaiberi/shared";
import { useMemo } from "react";

import { useSectionElementWarnings } from "./useSectionElementWarnings";
import { useSectionLookWarnings } from "./useSectionLookWarnings";
import { isSectionBlock } from "../../components/site-pages/block-meta";
import { makeSectionFormValue, type SectionFormContextValue } from "../../components/site-builder/section/section-form-context";
import type { SitePageDetail } from "../../lib/api-site-pages";

type Extra = NonNullable<Parameters<typeof makeSectionFormValue>[4]>;

interface Deps {
  blockId: string;
  payload: SectionPayload;
  theme: SiteTheme | null;
  page: SitePageDetail | null;
  issues: ReadonlyArray<{ path: string; message: string }>;
  warnings: Parameters<typeof makeSectionFormValue>[1];
  highlight: SectionFormContextValue["highlight"];
  /** Контекст секции и перенос элементов (`useSectionExtras`, мемоизировано). */
  formExtra: Extra;
  /** Переход к полю и живой предпросмотр. */
  reveal: Pick<Extra, "reveal" | "revealDone" | "revealPath" | "preview">;
}

export function useSectionFormValue({ blockId, payload, theme, page, issues, warnings, highlight, formExtra, reveal: jump }: Deps) {
  const look = useSectionLookWarnings(payload, theme, page?.media);
  const blocks = page?.blocks;
  const otherSections = useMemo(
    () => (blocks ?? []).filter((b) => b.id !== blockId && isSectionBlock(b)).map((b) => b.payload as SectionPayload),
    [blocks, blockId],
  );
  const elementWarnings = useSectionElementWarnings(payload, theme, otherSections);
  const { reveal, revealDone, revealPath, preview } = jump;
  const form = useMemo(
    () =>
      makeSectionFormValue(issues, warnings, highlight, theme, {
        ...formExtra,
        reveal,
        revealDone,
        revealPath,
        preview,
        lookWarnings: look.warnings,
        elementWarnings,
        mediaSizes: look.mediaSizes,
      }),
    [issues, warnings, highlight, theme, formExtra, reveal, revealDone, revealPath, preview, look, elementWarnings],
  );
  return { form, look, elementWarnings };
}
